import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from '../../../services/apiClient';
import { createSelector } from '@reduxjs/toolkit';

// Fetch all investments
export const fetchInvestments = createAsyncThunk(
  'investment/fetchAll',
  async ({ canvasId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/canvas/${canvasId}/investments?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Fetch single investment
export const fetchInvestment = createAsyncThunk(
  'investment/fetchOne',
  async ({ canvasId, investmentId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/canvas/${canvasId}/investments/${investmentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Create investment
export const createInvestment = createAsyncThunk(
  'investment/create',
  async ({ canvasId, investmentData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/canvas/${canvasId}/investments`, investmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Update investment
export const updateInvestment = createAsyncThunk(
  'investment/update',
  async ({ canvasId, investmentId, investmentData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/canvas/${canvasId}/investments/${investmentId}`, investmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);
 
// Delete investment
export const deleteInvestment = createAsyncThunk(
  'investment/delete',
  async ({ canvasId, investmentId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/canvas/${canvasId}/investments/${investmentId}`);
      return investmentId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Upload investments from file
export const uploadInvestments = createAsyncThunk(
  'investment/upload',
  async ({ canvasId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/canvas/${canvasId}/investments/upload`, formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

export const investmentOverview = createAsyncThunk(
  'investment/overview',
  async({canvasId}, {rejectWithValue})=>{
    try{
      const res = await apiClient.get(`/canvas/${canvasId}/investments/overview`)
      return res.data
    }catch(e){
      return rejectWithValue(e);
    }
  }
)

export const addTransactionToAsset = createAsyncThunk(
  'investment/addTransactionToAsset',
  async ({ canvasId, assetName, ticker, transactionData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/canvas/${canvasId}/investments/asset/${encodeURIComponent(assetName)}/${encodeURIComponent(ticker)}/transactions`,
        transactionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add transaction');
    }
  }
);

// Update transaction
export const updateTransaction = createAsyncThunk(
  'investment/updateTransaction',
  async ({ canvasId, transactionId, transactionData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        `/canvas/${canvasId}/investments/transaction/${transactionId}`,
        transactionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update transaction');
    }
  }
);

// Update asset
export const updateAsset = createAsyncThunk(
  'investment/updateAsset',
  async ({ canvasId, assetId, assetName, ticker, assetData }, { rejectWithValue }) => {
    try {
      let url;
      if (assetId) {
        url = `/canvas/${canvasId}/investments/asset/${assetId}`;
      } else {
        url = `/canvas/${canvasId}/investments/asset/${encodeURIComponent(assetName)}/${encodeURIComponent(ticker)}`;
      }
      const response = await apiClient.put(url, assetData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update asset');
    }
  }
);

export const deleteAssetWithTransactions = createAsyncThunk(
  'investment/deleteAssetWithTransactions',
  async ({ canvasId, assetName, ticker }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(
        `/canvas/${canvasId}/investments/asset/${encodeURIComponent(assetName)}/${encodeURIComponent(ticker)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete asset');
    }
  }
);

const investmentSlice = createSlice({
  name: "investment",
  initialState: {
    investments: [],
    investment: null,
    loading: false,
    error: null,
    pagination: null,
    uploadResult: null,
    overview:null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all investments
      .addCase(fetchInvestments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvestments.fulfilled, (state, action) => {
        state.loading = false;
        state.investments = action.payload.investments || action.payload.items || [];
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
        };
      })
      .addCase(fetchInvestments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Fetch single investment
      .addCase(fetchInvestment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvestment.fulfilled, (state, action) => {
        state.loading = false;
        state.investment = action.payload;
      })
      .addCase(fetchInvestment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Create investment
      .addCase(createInvestment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvestment.fulfilled, (state, action) => {
        state.loading = false;
        state.investments = [action.payload, ...state.investments];
      })
      .addCase(createInvestment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Update investment
      .addCase(updateInvestment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvestment.fulfilled, (state, action) => {
        state.loading = false;
        state.investments = state.investments.map((inv) =>
          inv._id === action.payload._id ? action.payload : inv
        );
      })
      .addCase(updateInvestment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Delete investment
      .addCase(deleteInvestment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvestment.fulfilled, (state, action) => {
        state.loading = false;
        state.investments = state.investments.filter((inv) => inv._id !== action.payload);
      })
      .addCase(deleteInvestment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Upload investments
      .addCase(uploadInvestments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadInvestments.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadInvestments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })
      .addCase(investmentOverview.fulfilled, (state, action)=>{
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(addTransactionToAsset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTransactionToAsset.fulfilled, (state, action) => {
        state.loading = false;
        state.investments.push(action.payload);
      })
      .addCase(addTransactionToAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update transaction
      .addCase(updateTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.investments.findIndex(inv => inv._id === action.payload._id);
        if (index !== -1) {
          state.investments[index] = action.payload;
        }
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAsset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAsset.fulfilled, (state, action) => {
        state.loading = false;
        const { assetName, ticker, assetData } = action.meta.arg || {};
        if (assetData) {
          state.investments = state.investments.map(inv => {
            if (inv.assetName === assetName && inv.ticker === ticker) {
              return {
                ...inv,
                assetName: assetData.assetName || inv.assetName,
                ticker: assetData.ticker || inv.ticker,
                type: assetData.type || inv.type,
                currency: assetData.currency || inv.currency,
                exchange: assetData.exchange !== undefined ? assetData.exchange : inv.exchange,
              };
            }
            return inv;
          });
        }
      })
      .addCase(updateAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })
      // Delete asset with all transactions
      .addCase(deleteAssetWithTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAssetWithTransactions.fulfilled, (state, action) => {
        state.loading = false;
        const { assetName, ticker } = action.meta.arg;
        state.investments = state.investments.filter(
          inv => !(inv.assetName === assetName && inv.ticker === ticker)
        );
      })
      .addCase(deleteAssetWithTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const selectInvestmentsData = (state) => state.investment.investments;
export const selectInvestmentLoadingState = (state) => state.investment.loading;
export const selectInvestmentOverviewData = (state) => state.investment.overview;
export const selectInvestmentPaginationData = (state) => state.investment.pagination;

export const selectInvestments = createSelector(
  [selectInvestmentsData],
  (investments) => investments || []
);

export const selectInvestmentLoading = createSelector(
  [selectInvestmentLoadingState],
  (loading) => loading || false
);

export const selectInvestmentOverview = createSelector(
  [selectInvestmentOverviewData],
  (overview) => overview || {}
);

export const selectInvestmentPagination = createSelector(
  [selectInvestmentPaginationData],
  (pagination) => pagination || {}
);

export default investmentSlice.reducer;
